import { ApartmentOutlined } from '@ant-design/icons'
import { Select } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { queryClient } from '../../../api/client'
import { useAvailableTenants } from '../../../api/hooks/useAvailableTenants'
import { useTenantStore } from '../../../stores/tenantStore'

const { Option } = Select

type NavTenantSelectorProps = {
  isExpanded?: boolean
}

export function NavTenantSelector({
  isExpanded = false,
}: NavTenantSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { selectedTenant, selectTenant } = useTenantStore()
  const { availableTenants, isLoadingTenants } = useAvailableTenants()
  const panelRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!selectedTenant) {
      const firstAvailableTenant = availableTenants[0]
      if (firstAvailableTenant) {
        selectTenant(firstAvailableTenant)
      }
    }
  }, [selectedTenant, availableTenants])

  const hasMultipleTenants = availableTenants.length > 1

  // Filter out Gravitate tenant from options
  const tenantOptions = availableTenants.filter(
    (tenant) =>
      tenant &&
      typeof tenant === 'object' &&
      tenant.name &&
      tenant.name !== 'Gravitate',
  )

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(target)
      const isOutsidePanel =
        panelRef.current && !panelRef.current.contains(target)
      const isInsideDropdown = (target as Element).closest?.(
        '.ant-select-dropdown',
      )

      if (isOutsideMenu && isOutsidePanel && !isInsideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleTenantSelect = (tenantName: string) => {
    const tenant = availableTenants.find((t) => t.name === tenantName)
    if (tenant) {
      selectTenant(tenant)
      queryClient.invalidateQueries()
      setIsOpen(false)
    }
  }

  const currentTenantDisplay = isLoadingTenants
    ? 'Loading...'
    : selectedTenant?.name || 'Select Tenant'

  function getMenuPosition() {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect()
      return {
        bottom: window.innerHeight - rect.top + 8,
        left: isExpanded ? rect.left : 68,
      }
    }
    return { bottom: 100, left: 68 }
  }

  const menuPosition = getMenuPosition()

  // Don't render if only one tenant
  if (availableTenants.length <= 1) {
    return null
  }

  if (isExpanded) {
    return (
      <>
        <div
          ref={panelRef}
          onClick={() => hasMultipleTenants && setIsOpen(!isOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '4px',
            marginBottom: 8,
            borderRadius: 10,
            backgroundColor: isOpen
              ? 'var(--nav-route-hover-bg)'
              : 'var(--nav-route-active-bg)',
            cursor: hasMultipleTenants ? 'pointer' : 'default',
            transition: 'background-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            if (!isOpen && hasMultipleTenants) {
              e.currentTarget.style.backgroundColor =
                'var(--nav-route-hover-bg)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isOpen) {
              e.currentTarget.style.backgroundColor =
                'var(--nav-route-active-bg)'
            }
          }}
        >
          <div style={{ position: 'relative', flexShrink: 0, padding: 2 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--theme-color-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ApartmentOutlined style={{ fontSize: 16, color: 'white' }} />
            </div>
          </div>
          <div
            style={{
              overflow: 'hidden',
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--nav-route-active-color)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {currentTenantDisplay}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--nav-route-color)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Current Tenant
            </div>
          </div>
        </div>

        {isOpen && (
          <div
            ref={menuRef}
            className='user-menu-popout'
            style={{
              position: 'fixed',
              bottom: menuPosition.bottom,
              left: menuPosition.left,
              backgroundColor: 'var(--nav-bg)',
              borderRadius: 10,
              boxShadow:
                '0 2px 8px rgba(0, 0, 0, 0.15), 0 4px 24px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '12px',
              minWidth: 220,
              zIndex: 1001,
              animation: 'popoutSlideIn 0.15s ease-out',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--nav-route-color)',
                marginBottom: 8,
                textTransform: 'uppercase',
              }}
            >
              Select Tenant
            </div>
            <Select
              onChange={handleTenantSelect}
              placeholder='Select a tenant'
              showSearch
              optionFilterProp='children'
              value={selectedTenant?.name}
              style={{ width: '100%' }}
              size='small'
            >
              {tenantOptions.map((tenant) => (
                <Option value={tenant.name} key={tenant.name}>
                  {tenant.name}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div
        ref={panelRef}
        onClick={() => hasMultipleTenants && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '0 4px 8px',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'var(--theme-color-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: hasMultipleTenants ? 'pointer' : 'default',
          }}
        >
          <ApartmentOutlined style={{ fontSize: 16, color: 'white' }} />
        </div>
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className='user-menu-popout'
          style={{
            position: 'fixed',
            bottom: menuPosition.bottom,
            left: menuPosition.left,
            backgroundColor: 'var(--nav-bg)',
            borderRadius: 10,
            boxShadow:
              '0 2px 8px rgba(0, 0, 0, 0.15), 0 4px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '12px',
            minWidth: 220,
            zIndex: 1001,
            animation: 'popoutSlideIn 0.15s ease-out',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--nav-route-color)',
              marginBottom: 8,
              textTransform: 'uppercase',
            }}
          >
            Select Tenant
          </div>
          <Select
            onChange={handleTenantSelect}
            placeholder='Select a tenant'
            showSearch
            optionFilterProp='children'
            value={selectedTenant?.name}
            style={{ width: '100%' }}
            size='small'
          >
            {tenantOptions.map((tenant) => (
              <Option value={tenant.name} key={tenant.name}>
                {tenant.name}
              </Option>
            ))}
          </Select>
        </div>
      )}
    </>
  )
}
